import { useEffect, useState } from 'react';

/** Which line the screen is showing. Every one of them is chosen by the clock, not by the server. */
export type ProcessingStage = 'uploading' | 'scanning' | 'processing';

export const SCANNING_AT_MS = 3_000;
export const PROCESSING_AT_MS = 6_000;

/**
 * When the screen stops expecting the member to wait for it.
 *
 * Late enough that a batch which finishes normally is never told to go away, and early enough
 * that a member watching a slow one is not left guessing whether they have to.
 */
export const CLOSE_HINT_AT_MS = 10_000;

/** In order, so the wake-up below is "the next one we have not reached" and cannot fall out of step. */
const BOUNDARIES_MS = [SCANNING_AT_MS, PROCESSING_AT_MS, CLOSE_HINT_AT_MS];

interface ProcessingNarration {
    readonly stage: ProcessingStage;
    /** Whether to add the note telling the member they can walk away. */
    readonly showCloseHint: boolean;
}

/**
 * How far into the wait the screen is.
 *
 * Timed rather than observed, for all four windows. Sending a file and reading it are one
 * indistinguishable wait as far as the server is concerned — there is no per-file "scanning"
 * signal to observe — so the whole narration is a fixed sequence that plays while the real work
 * is on its way, and the last window simply holds until the caller takes the overlay down.
 *
 * Anchored to the *upload* and not to this component. The modal unmounts when it closes, so
 * timing from mount alone would replay the sequence every time a member reopened it — including
 * on a batch that has been with the server for a minute, where the screen would go back to
 * "Uploading" over files that were read long ago. `startedAtMs` is the batch's own `uploadedAt`,
 * so reopening lands past the early windows on its first render.
 *
 * The anchor only ever moves earlier. It starts at mount — which, before any document exists, is
 * the only truth available — and takes `startedAtMs` when that turns out to be older. Left to
 * move forward it would run the clock backwards: `startedAtMs` is read off the oldest file still
 * waiting, so the first file of a batch being answered for would drag it towards the present and
 * push the narration back to its first line.
 */
export default function useProcessingStage(startedAtMs?: number): ProcessingNarration {
    const [nowMs, setNowMs] = useState(() => Date.now());

    // State rather than a ref, adjusted during render in the sanctioned way: React discards the
    // update from a render it abandons, where a ref mutation would survive it and leave the anchor
    // permanently earlier than anything that actually committed. An effect was the other option
    // and is worse here — the anchor would miss the first render, so reopening on an old batch
    // would flash the first line before correcting, which is the thing it exists to prevent.
    const [anchorMs, setAnchorMs] = useState(() => Math.min(nowMs, startedAtMs ?? Infinity));
    if (startedAtMs !== undefined && startedAtMs < anchorMs) {
        setAnchorMs(startedAtMs);
    }

    // Clamped because the anchor can be the server's clock and this is the browser's. A server
    // running seconds ahead would otherwise read as negative elapsed time.
    const elapsedMs = Math.max(0, nowMs - anchorMs);

    let stage: ProcessingStage = 'processing';
    if (elapsedMs < SCANNING_AT_MS) {
        stage = 'uploading';
    } else if (elapsedMs < PROCESSING_AT_MS) {
        stage = 'scanning';
    }

    const nextBoundaryMs = BOUNDARIES_MS.find((boundaryMs) => boundaryMs > elapsedMs);

    useEffect(() => {
        // Past the last boundary there is nothing left for this clock to change, so it stops
        // itself. Reopening on an older batch arrives here already past it and never arms a
        // timer at all.
        if (nextBoundaryMs === undefined) {
            return undefined;
        }
        // One wake-up per window rather than a polling interval: the copy changes only at these
        // three moments, so they are the only renders this clock owes anyone.
        const timer = setTimeout(() => setNowMs(Date.now()), nextBoundaryMs - elapsedMs);
        return () => clearTimeout(timer);
    }, [nextBoundaryMs, elapsedMs]);

    return { stage, showCloseHint: elapsedMs >= CLOSE_HINT_AT_MS };
}
